import { useMemo } from 'react';
import { useReportStore } from '../../../stores/useReportStore';
import KPICard from '../widgets/KPICard';
import Section from '../widgets/Section';
import StatusPill from '../widgets/StatusPill';
import { toneFor } from '../widgets/tone';
import StrategyPillar from '../widgets/StrategyPillar';
import { formatMoney } from '../../../utils/formatMoney';

export default function StrategyRoadmap() {
  const data = useReportStore((s) => s.reportData);
  if (!data) return null;

  const raw = data as unknown as Record<string, unknown>;
  const pillars = Array.isArray(raw.pillars) ? raw.pillars : [];
  const gaps = Array.isArray(raw.gaps) ? raw.gaps : [];

  // Compute investment summary from pillar data
  const investmentSummary = useMemo(() => {
    if (pillars.length === 0) return null;
    let totalReturn = 0;
    let totalInvestment = 0;
    let totalPayback = 0;
    let paybackCount = 0;
    for (const p of pillars as any[]) {
      const ret = parseFloat(String(p.expectedReturn || '0').replace(/[^0-9.]/g, ''));
      const inv = parseFloat(String(p.investment || '0').replace(/[^0-9.]/g, ''));
      if (ret) totalReturn += ret * 1000; // assume K suffix
      if (inv) totalInvestment += inv * 1000;
      // Estimate payback from return/investment ratio
      if (inv > 0 && ret > 0) {
        totalPayback += (inv / ret) * 12;
        paybackCount++;
      }
    }
    const avgPayback = paybackCount > 0 ? (totalPayback / paybackCount).toFixed(1) : '--';
    const roi = totalInvestment > 0 ? ((totalReturn / totalInvestment)).toFixed(1) : '--';
    const fmtK = (n: number) => formatMoney(n, data?._currency);
    return [
      { value: `+${fmtK(totalReturn)}`, label: 'Projected ARR Uplift' },
      { value: fmtK(totalInvestment) + '/mo', label: 'Total Investment' },
      { value: `${avgPayback} mo`, label: 'Avg Payback Period' },
      { value: `${roi}x`, label: 'Projected 12-mo ROI' },
    ];
  }, [pillars, data?._currency]);

  if (pillars.length === 0 && gaps.length === 0) {
    return <p className="text-muted-foreground p-8 text-center">No strategy data available.</p>;
  }

  return (
    <div>
      {/* Investment Summary */}
      {investmentSummary && (
        <section className="mb-6.5">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5">
            {investmentSummary.map((item) => (
              <KPICard key={item.label} {...item} />
            ))}
          </div>
        </section>
      )}

      {pillars.length > 0 && (
        <Section
          title="Strategic pillars"
          hint="Key initiatives prioritized by impact and feasibility — expand each pillar for the execution plan."
        >
          <div className="space-y-2">
            {pillars.map((pillar: any, i: number) => (
              <StrategyPillar key={pillar.title || i} pillar={pillar} index={i} />
            ))}
          </div>
        </Section>
      )}

      {gaps.length > 0 && (
        <Section
          title="Critical gaps"
          hint="Capability gaps that need to be addressed to achieve the strategic objectives."
        >
          <div className="space-y-2">
            {gaps.map((gap: any, i: number) => (
              <div className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3.5" key={i}>
                <StatusPill tone={toneFor(String(gap.severity || ''))} className="shrink-0">
                  {String(gap.severity || '')}
                </StatusPill>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold tracking-[-0.01em] text-foreground">{String(gap.title || '')}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{String(gap.area || '')}</div>
                </div>
                <div className="shrink-0 text-xs text-muted-foreground">{String(gap.pillar || '')}</div>
                <div className="shrink-0 font-mono text-[11px] font-medium text-foreground/80">{String(gap.timeline || '')}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
